import { ChevronDown, ChevronUp } from "lucide-react";
import { ReactNode } from "react";
import ReactDOM from "react-dom";
import { cn } from "../../../lib/utils";
import { useDropdown } from "./useDropDown";

interface BaseDropdownProps {
  trigger: ReactNode;
  children: ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  className?: string;
  portalContainer?: Element;
  disabled?: boolean;
  buttonClassName?: string;
}

export function BaseDropdown({
  trigger,
  children,
  isOpen,
  onToggle,
  className,
  portalContainer = document.body,
  minWidth = 160,
  disabled = false,
  buttonClassName,
}: BaseDropdownProps & { minWidth?: number }) {
  const { triggerRef, dropdownRef, dropdownPosition, toggleDropdown } =
    useDropdown({
      isOpen,
      onToggle,
      portalContainer,
    });

  const dropdownMenu = isOpen && dropdownPosition && (
    <div
      ref={dropdownRef}
      className="fixed z-[9999] bg-white border border-gray-200 rounded-md shadow-lg"
      style={{
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        width: `${Math.max(dropdownPosition.width, minWidth)}px`,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );

  return (
    <div className={cn("relative", className)}>
      <button
        ref={triggerRef}
        onClick={() => {
          if (disabled) return;
          toggleDropdown();
        }}
        disabled={disabled}
        className={cn(
          "w-full px-3 py-2 border rounded-md text-sm flex items-center justify-between text-left",
          isOpen
            ? "border-blue-500 bg-blue-50 text-blue-600 ring-1 ring-blue-500"
            : "border-gray-300 bg-white hover:bg-gray-50 text-gray-700",
          disabled && "opacity-50 cursor-not-allowed",
          buttonClassName
        )}
      >
        {trigger}
        {isOpen ? (
          <ChevronUp className="h-4 w-4 ml-2 flex-shrink-0 text-gray-500" />
        ) : (
          <ChevronDown className="h-4 w-4 ml-2 flex-shrink-0 text-gray-500" />
        )}
      </button>
      {portalContainer && ReactDOM.createPortal(dropdownMenu, portalContainer)}
    </div>
  );
}
